import './index.css'

import isEqual from 'lodash/isEqual'
import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { NavigateFunction, useNavigate, useParams } from 'react-router-dom'

import { templates } from '@disco/content-editor/default'
import {
  campaignDetailsResetTemplate,
  campaignDetailsSave,
} from '@disco/state-management/actions/campaign-details-actions'
import { setTemplateIfUnset } from '@disco/state-management/actions/campaign-editor-actions'
import { getCampaign } from '@disco/state-management/selectors/campaigns'

import type { EmailCampaign } from 'types/campaign'
import type { EmailTemplate } from 'types/content-editor'
import type { State } from 'types/redux'

import DocumentHeadTags from 'components/document-head-tags'
import Heading from 'components/ui/heading'
import ModalDialog from 'components/ui/modal-dialog'
import PageContainer from 'components/ui/page-container'

import CampaignHeader from 'pages/campaigns/campaign-header'
import CampaignNav, { CHOOSE_TEMPLATE_TAB } from 'pages/campaigns/campaign-nav'
import EditScheduledEmailModal from 'pages/campaigns/edit-scheduled-email-modal'

import type { RootState } from 'store/configureStore'

import CampaignTemplatePreview from './campaign-template-preview'

function mapStateToProps(
  state: RootState,
  ownProps: {
    params: {
      campaignId: string
    }
  },
) {
  return {
    campaign: getCampaign(state, {
      campaignId: parseInt(ownProps.params.campaignId),
    }),
  }
}

const mapDispatchToProps = {
  campaignDetailsResetTemplate,
  campaignDetailsSave,
  setTemplateIfUnset,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>

interface AdditionalProps {
  navigate: NavigateFunction
  params: {
    campaignId: string
  }
}

type Props = PropsFromRedux & AdditionalProps

class CampaignChooseTemplatePage extends React.Component<
  Props,
  {
    template: string | null | undefined
    overwriteModal: boolean
  }
> {
  state = {
    template: this.props.campaign?.template || templates[0].id,
    overwriteModal: false,
  }
  _handleOverwritePrompt = () => {
    const { campaign } = this.props
    if (!campaign) return

    // if empty - overwrite
    if (
      campaign.template_data &&
      campaign.template_data.campaignEditor.sections.length === 0
    ) {
      this._handleOverwriteConfirm()

      return
    }

    // if not touched - overwrite
    if (
      templates.some(
        t =>
          campaign.template_data &&
          isEqual(t.layout, campaign.template_data.campaignEditor.sections),
      )
    ) {
      this._handleOverwriteConfirm()

      return
    }

    if (!this.state.template && !this._hasKnownTemplate()) {
      this._proceed()

      return
    }

    if (this.state.template === campaign.template) {
      this._proceed()

      return
    }

    this.setState({
      overwriteModal: true,
    })
  }
  _handleOverwriteConfirm = () => {
    if (!this.state.template) return
    this.props.campaignDetailsResetTemplate(
      parseInt(this.props.params.campaignId),
      this.state.template,
    )

    this._proceed()
  }
  _proceed = () => {
    const { campaign } = this.props
    if (!campaign) return
    // performSave notifies then rethrows; nothing here can act on it
    if (campaign.status !== 'sent')
      Promise.resolve(this.props.campaignDetailsSave(campaign.id, true)).catch(
        () => {},
      )
    this.props.navigate(`/campaigns/${this.props.params.campaignId}/editor`)
  }
  _handleCancel = () => {
    this.setState({
      overwriteModal: false,
    })
  }

  _hasKnownTemplate() {
    if (!this.props.campaign) return false
    const currentTemplate = this.props.campaign.template
    return templates.some(t => t.id === currentTemplate)
  }

  componentDidMount() {
    const { campaign } = this.props
    if (!campaign) return
    this.props.setTemplateIfUnset(campaign.id)
  }

  render() {
    let overwriteModal

    if (this.state.overwriteModal) {
      overwriteModal = (
        <ModalDialog
          title="Switching templates"
          primaryAction={{
            caption: 'Switch template',
            onClick: this._handleOverwriteConfirm,
          }}
          status="danger"
          onCancel={this._handleCancel}
        >
          <p>
            <b>Warning!</b> Changing the template will remove any content you’ve
            added for this campaign.
          </p>
          If you’d like to keep your content but change the layout, you can do
          that by adding and removing blocks in the <b>Edit content</b> step.
        </ModalDialog>
      )
    }

    return (
      <div className="Page_Content">
        <DocumentHeadTags title="Choose template | DISCO" />
        {overwriteModal}
        <CampaignHeader
          campaignId={this.props.params.campaignId}
          nextDisabled={!this.state.template && !this._hasKnownTemplate()}
          onNextClick={this._handleOverwritePrompt}
        />
        <PageContainer>
          <CampaignNav
            campaignId={this.props.params.campaignId}
            activeStep={CHOOSE_TEMPLATE_TAB}
          />
          <PageContainer.Section>
            <Heading size={500}>Select a template</Heading>
            <div className="form-note">
              Select a template here to get started. In the next step, you can
              customise the content, look and feel of your email.
            </div>
            <div className="CampaignChooseTemplate_Wrap">
              {templates.map(template => (
                <div key={template.id} className="CampaignChooseTemplate_Card">
                  <CampaignTemplatePreview
                    selected={template.id === this.state.template}
                    template={template as EmailTemplate}
                    onSelect={() => {
                      if (template.id === this.state.template) {
                        return this.setState({
                          template: null,
                        })
                      }

                      this.setState({
                        template: template.id,
                      })
                    }}
                  />
                </div>
              ))}
            </div>
          </PageContainer.Section>
          <EditScheduledEmailModal campaignId={this.props.params.campaignId} />
        </PageContainer>
      </div>
    )
  }
}

const ConnectedCampaignChooseTemplatePage = connector(
  CampaignChooseTemplatePage,
)

function CampaignChooseTemplatePageWithRouter() {
  const navigate = useNavigate()
  const params = useParams<{ campaignId: string }>()
  return (
    <ConnectedCampaignChooseTemplatePage
      navigate={navigate}
      params={params as { campaignId: string }}
    />
  )
}

export default CampaignChooseTemplatePageWithRouter
